import * as Yup from "yup";
import { TFunction } from "i18next";
import { PhoneNumberUtil } from "google-libphonenumber";
import { strictEmailRegex } from "@me-team/host/src/utils/utils";
import { DateTime } from "luxon";
const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
    try {
        return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
        return false;
    }
};

const productItemSchema = (t: TFunction, isUpdate = false) =>
    Yup.object().shape({
        product: Yup.number().required(t("validation.required")),
        warehouse: Yup.number().required(t("validation.required")),
        quantity: Yup.number()
            .required(t("validation.required"))
            .min(isUpdate ? 0 : 1, t("validation.required")),
        unit: Yup.number().required(t("validation.required")),
        price: Yup.number().required(t("validation.required")).min(0.01, t("validation.required")),
        discount: Yup.number()
            .min(0, t("Enter a value between 0 and 100."))
            .max(100, t("Enter a value between 0 and 100.")),
    });

export const saleProductsValidationSchema = (t: TFunction, timeZoneName: string, isUpdate = false) =>
    Yup.object().shape({
        dateTime: Yup.string()
            .required(t("validation.required"))
            .test("is-not-future", t("Date and time must not be later than the current one"), function (value) {
                if (!value) return false;

                const inputDate = DateTime.fromFormat(value, "yyyy-MM-dd HH:mm:ss", { zone: timeZoneName });
                const now = DateTime.now().setZone(timeZoneName);

                return inputDate.isValid && inputDate <= now;
            }),
        employee: Yup.number().required(t("validation.required")),

        client: Yup.object()
            .shape({
                name: Yup.string()
                    .nullable()
                    .transform((value) => (value === null ? "" : value))
                    .test("name-required", t("validation.required"), function (value) {
                        const { phone, email } = this.parent;
                        const hasPhone = !!phone?.trim();
                        const hasEmail = !!email?.trim();

                        if ((hasPhone || hasEmail) && !value?.trim()) {
                            return this.createError({ message: t("validation.required") });
                        }
                        return true;
                    }),
                phone: Yup.string()
                    .nullable()
                    .transform((value) => (value === null ? "" : value))
                    .test("phone-valid", t("Phone number is not valid"), function (value) {
                        const trimmed = value?.trim() ?? "";
                        if (trimmed === "") return true;
                        const isOnlyDialCode = /^\+\d{1,4}$/.test(trimmed);
                        if (isOnlyDialCode) return true;
                        if (!isPhoneValid(trimmed)) {
                            return this.createError({ message: t("Phone number is not valid") });
                        }
                        return true;
                    })
                    .test(
                        "contact-required",
                        t("validation.at least one of the fields phone or email must be filled"),
                        function (value) {
                            const { name, email } = this.parent;
                            if (!!name?.trim() && !value?.trim() && !email?.trim()) {
                                return this.createError({
                                    message: t("validation.at least one of the fields phone or email must be filled"),
                                });
                            }
                            return true;
                        }
                    ),
                email: Yup.string()
                    .nullable()
                    .transform((value) => (value === null ? "" : value))
                    .test("email-format-or-empty", t("validation.requiredEmail"), function (value) {
                        const val = value?.trim() ?? "";
                        if (val === "") return true;
                        return strictEmailRegex.test(val);
                    })
                    .test(
                        "contact-required",
                        t("validation.at least one of the fields phone or email must be filled"),
                        function (value) {
                            const { name, phone } = this.parent;
                            if (!!name?.trim() && !value?.trim() && !phone?.trim()) {
                                return this.createError({
                                    message: t("validation.at least one of the fields phone or email must be filled"),
                                });
                            }
                            return true;
                        }
                    ),
            })
            .nullable(),
        productsInput: Yup.array().of(productItemSchema(t, isUpdate)),
        isPaid: Yup.boolean(),
        paymentType: Yup.string().when("isPaid", {
            is: true,
            then: (schema) => schema.required(t("validation.required")),
            otherwise: (schema) => schema.notRequired(),
        }),
        comment: Yup.string().nullable(),
    });
